import React, { useCallback, useContext } from 'react'
import cn from 'classnames'
import { Footer, RegulationZone } from '@grammarly/website-components'
import { useExperimentService } from '../../lib/hooks'
import { gnarClient, gnarSpec, EventProps } from '../../lib/tracking/gnar'
import { logError, LogErrorMetrics } from '../../lib/tracking/felog'
import { transformToObject } from '../../lib/utils/error'
import { getConfig } from '../../config'
import { PageContext } from '../../contexts/page-context'
import { usePageTheme } from '../../contexts/page-theme-context'

import pageSectionStyles from '../ui-lib/page-section/page-section.module.scss'
import { Themes } from '../../contexts/section-theme-context'

const baseDomain = getConfig().domains.main
const shBaseDomain = getConfig().domains.superhuman

interface PageFooterProps {
  className?: string
  theme?: Themes
}

export const PageFooter: React.FC<PageFooterProps> = ({ className, theme }) => {
  const { pageId, slug } = useContext(PageContext)
  const { isGDPR, isUSA, isAirGapEnabled, isAirGapFooterEnabled, isUpdateOpeningsPageUrls_25Enabled } =
    useExperimentService()
  const { pageWidth } = usePageTheme()

  const gnarTrackFn = useCallback(
    (trackEventProps: EventProps) => {
      return gnarClient.track({ ...trackEventProps, pageId, pageSlug: slug })
    },
    [pageId, slug]
  )

  const onOpenCookieSettings = useCallback(
    async (label: string) => {
      try {
        if (isAirGapEnabled && isAirGapFooterEnabled) {
          const regimes = window.airgap?.getRegimes?.()
          if (regimes?.has('USA')) {
            await window?.transcend?.showConsentManager?.({ viewState: 'CompleteOptionsInverted' })
          } else {
            await window?.transcend?.showConsentManager?.({ viewState: 'CompleteOptionsToggles' })
          }
        } else {
          window?.OneTrust?.ToggleInfoDisplay?.()
        }
        gnarSpec.footerItemClick(label, pageId, slug)
      } catch (e) {
        logError('Error opening ' + label, isAirGapEnabled ? LogErrorMetrics.Transcend : LogErrorMetrics.OneTrust, {
          error: transformToObject(e as Error),
        })
      }
    },
    [pageId, slug, isAirGapEnabled, isAirGapFooterEnabled]
  )

  const regulationZone = isUSA ? RegulationZone.USA : RegulationZone.GDPR

  return (
    <Footer
      clientName="cms"
      baseDomain={baseDomain}
      shBaseDomain={shBaseDomain}
      gnarTrackFn={gnarTrackFn}
      onClickDoNotSell={isGDPR || isUSA ? () => onOpenCookieSettings('doNotSell') : undefined}
      regulationZone={regulationZone}
      className={cn(
        pageWidth === '1280' ? pageSectionStyles.pagePaddingContainer1280 : pageSectionStyles.pagePaddingContainer1080,
        className
      )}
      contentClassName={cn(
        pageWidth === '1280'
          ? pageSectionStyles.pageContentWidth1280Container
          : pageSectionStyles.pageContentWidth1080Container
      )}
      hasDarkThemeBG={theme === Themes.Dark}
      isUpdateOpeningsPageUrl={isUpdateOpeningsPageUrls_25Enabled}
    />
  )
}
